import React, { createContext, useContext, useState, useCallback } from 'react';

// Тема: темна за замовчуванням, світла — перемикачем. Вибір пам'ятається в
// браузері. Атрибут на <html> ставиться одразу при завантаженні модуля, ще до
// першого рендера, — інакше сторінка на мить блимала б темною перед світлою.
const STORAGE_KEY = 'theme';

const readStored = () => {
    try {
        return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
    } catch (e) {
        return 'dark';
    }
};

const apply = (theme) => {
    document.documentElement.dataset.theme = theme;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'light' ? '#F2F2F5' : '#0B0B0D');
};

apply(readStored());

const ThemeContext = createContext({ theme: 'dark', toggleTheme: () => {} });

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(readStored);

    const toggleTheme = useCallback(() => {
        setTheme(prev => {
            const next = prev === 'dark' ? 'light' : 'dark';
            // Атрибут — синхронно, до рендера: графіки читають кольори зі змінних
            // CSS під час рендера (useChartColors) і мають бачити вже нову тему.
            apply(next);
            try { localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* приватний режим */ }
            return next;
        });
    }, []);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => useContext(ThemeContext);

// Recharts малює SVG з кольорами-рядками, тому змінні CSS для графіків
// читаємо тут і перечитуємо при зміні теми.
const CHART_VARS = ['text', 'text-2', 'text-3', 'line', 'line-strong', 'surface', 'surface-2',
                    'surface-3', 'accent', 'ink', 'good', 'bad', 'down', 'up', 'warn'];

export const useChartColors = () => {
    const { theme } = useTheme();
    const [cache] = useState(() => new Map());
    if (!cache.has(theme)) {
        const style = getComputedStyle(document.documentElement);
        const colors = {};
        CHART_VARS.forEach(v => {
            // text-2 → text2, line-strong → lineStrong
            colors[v.replace(/-(\w)/g, (_, c) => c.toUpperCase())] = style.getPropertyValue(`--${v}`).trim();
        });
        cache.set(theme, colors);
    }
    return cache.get(theme);
};
